// Fetch wrapper that attaches the current Firebase ID token to every /api call.
//
// Callers use api<T>() for JSON reads/writes. Raw byte uploads (media) use
// apiRaw() to keep the Bearer-token attach logic here without forcing a JSON
// body encoding.
import { auth } from "@/auth/firebase";

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public body: unknown,
  ) {
    super(`${status}: ${detail}`);
  }
}

async function getIdToken(): Promise<string> {
  const u = auth.currentUser;
  if (!u) throw new ApiError(401, "Not signed in", null);
  return u.getIdToken();
}

async function parseError(res: Response): Promise<never> {
  let detail = res.statusText;
  let body: unknown = null;
  try {
    body = await res.json();
    if (body && typeof body === "object" && "detail" in body) {
      detail = String((body as { detail: unknown }).detail);
    }
  } catch {
    // response wasn't JSON — leave detail as statusText
  }
  throw new ApiError(res.status, detail, body);
}

export interface ApiOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  query?: Record<string, string | undefined>;
  headers?: Record<string, string>;
}

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const token = await getIdToken();
  const url = withQuery(path, opts.query);
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(opts.headers ?? {}),
  };
  const init: RequestInit = { method: opts.method ?? "GET", headers };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(opts.body);
  }
  const res = await fetch(url, init);
  if (!res.ok) await parseError(res);
  // 204 or empty body — return undefined-as-T for the caller. TanStack Query
  // handles undefined correctly.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function apiRaw(
  path: string,
  body: Blob | ArrayBuffer,
  extraHeaders: Record<string, string> = {},
): Promise<Response> {
  const token = await getIdToken();
  const res = await fetch(path, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      ...extraHeaders,
    },
    body,
  });
  if (!res.ok) await parseError(res);
  return res;
}

function withQuery(
  path: string,
  query: Record<string, string | undefined> | undefined,
): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v != null) params.set(k, v);
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}
